import { getObjectHash, isNonNaNNumber } from '@iotv/iotv-v3-types';
import { getScheduledUtcPeriodAtTimestampInput, decodeHexStringifyiedSchedules, getDeviceCondition, DeviceCondition } from '@iotv/lfc-devices';
import { checkBadEnabledDateRange } from './checkBadDates';
const debug = process.env.REACT_APP_DEBUG && false;
const extractedStateKeys = [
    'accelerometerActive',
    'activePattern',
    'isOperating',
    'runawayError',
    'downlinkAck',
    'rebooted',
    'boostSupplyError',
    'linkCheckErr',
    'movementNotDetectedError',
    'scheduleError',
    'solonoidDisconnectedError',
    'timeNotSet',
];
export const isExtractedState = (x) => !!x && isNonNaNNumber(x.timestamp) && extractedStateKeys.every((k) => !!x[k]);
const usePastSequentialConfigs = false;
/** reduces extracted state to an ordered array of time periods with metadata about state */
export const stateToPeriodOperatingState = ({ stateSnapshots }) => {
    const unMergedPeriodOperatingStates = stateSnapshots.reduce((a, c, i, arr) => {
        // treat  0 - 1 as the first period with the reading of 1
        if (i > 0) {
            const lastReading = arr[i - 1];
            const { state: { isOperating, accelerometerActive, encodedCurrentConfig, runawayError, activePattern, boostSupplyError, downlinkAck, linkCheckErr, movementNotDetectedError, rebooted, scheduleError, solonoidDisconnectedError, timeNotSet, }, timestamp, } = c;
            const { operatingState, errorsAndWarnings } = getDeviceCondition(c.state);
            const thisReadingPeriod = [new Date(lastReading.timestamp), new Date(timestamp)];
            if (thisReadingPeriod.at(0) < thisReadingPeriod.at(1)) {
                // if activePattern is trustworthy an additional predicate before decoding would reduce the calculation cost here.
                if (usePastSequentialConfigs && encodedCurrentConfig) {
                    // NOTE THIS IS CURRENTLY PREDICATED OUT!! IT MAY PRODUCE MISLEADING DISPLAY DATA
                    // get the reported scheulde at this timestamp
                    const schedules = decodeHexStringifyiedSchedules({ hexString: encodedCurrentConfig });
                    if (schedules.length > 0) {
                        const enabledDateRanges = schedules
                            .filter((schedule) => !!schedule.enable)
                            .map((schedule) => getScheduledUtcPeriodAtTimestampInput({ timestamp, schedule }))
                            .flat(1);
                        if (enabledDateRanges.length > 0) {
                            if (enabledDateRanges.length > 1) {
                                debug && console.log(`WARN: ${enabledDateRanges.length} enabledDateRanges `, new Date(timestamp), enabledDateRanges);
                            }
                            /** this will yeild periods when entity was scheduled to operate */
                            a.push(...enabledDateRanges.map((dr) => {
                                if (dr.enabled && !operatingState.includes(DeviceCondition.NORMAL_ON)) {
                                    debug && console.log(`WARN: was scheduled to be on but `, operatingState);
                                }
                                return {
                                    // dont use the dr range as will produce duplicate ranged items
                                    ...dr,
                                    operatingState,
                                    errorsAndWarnings,
                                    dateRange: thisReadingPeriod,
                                };
                            }));
                        }
                        else {
                            a.push({ dateRange: thisReadingPeriod, enabled: false, operatingState, errorsAndWarnings });
                        }
                    }
                    else {
                        a.push({ dateRange: thisReadingPeriod, enabled: false, operatingState, errorsAndWarnings });
                    }
                }
                else {
                    // nothing was apparently scheduled, so any activity likely to be an issue
                    a.push({ dateRange: thisReadingPeriod, enabled: false, operatingState, errorsAndWarnings });
                }
            }
            else {
                debug && console.log(`WARN history has out of order timestamps`, i, thisReadingPeriod.at(0)?.toUTCString());
            }
        }
        return a;
    }, []);
    unMergedPeriodOperatingStates.sort((a, b) => (a.dateRange.at(0) < b.dateRange.at(0) ? -1 : 1));
    const periodOperatingStates = unMergedPeriodOperatingStates.reduce((a, next) => {
        const last = a.at(a.length - 1);
        const nextStart = next.dateRange?.at(0);
        const nextEnd = next.dateRange?.at(1);
        const lastEnd = last?.dateRange?.at(1);
        const lastState = last?.operatingState ?? {};
        const nextState = next.operatingState;
        const matchingOperatingStates = getObjectHash({ ...last?.operatingState, ...last?.errorsAndWarnings }) === getObjectHash({ ...next.operatingState, ...next.errorsAndWarnings });
        if (last && lastEnd && nextStart && nextEnd && nextStart <= lastEnd && matchingOperatingStates) {
            last.dateRange[1] = nextEnd;
            // and dont push c
            debug && console.log(`stateToPeriodOperatingState merged`, { last, next });
        }
        else if (next.operatingState.length === 0 && next.errorsAndWarnings.length === 0) {
            /* UNDER TEST CURRENTLY LOOKS OK: DONT REPORT PAST NON EXCEPTION, NON OPERATING STATES*/
            // don't push
        }
        else {
            /* End of UNDER TEST */
            a.push(next);
        }
        return a;
    }, []);
    if (process.env.NODE_ENV === 'development')
        checkBadEnabledDateRange(periodOperatingStates, 'stateToPeriodOperatingState');
    debug && console.log('stateToPeriodOperatingState returning', { periodOperatingStates });
    return periodOperatingStates;
};
